import { ScaleElement } from '@scale/base-element';
import { Events } from '@scale/events';

class ScaleHeader extends ScaleElement {
  #scrollY = 0;
  #headerHeight = 0;
  #sentinel;
  #stickyObserver;
  #resizeObserver;

  setup() {
    const sticky = this.dataset.sticky || 'none';

    this.#measureHeight();
    this.#setupResizeObserver();

    if (sticky !== 'none') {
      this.#setupSticky(sticky);
    }

    this.#swapColorScheme(false);

    this.onDocument(Events.CART_UPDATED, (e) => this.#updateCartCount(e));

    this.onDocument('shopify:section:load', () => {
      this.#measureHeight();
    });
  }

  teardown() {
    this.#stickyObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#sentinel?.remove();
  }

  updatedCallback() {
    this.invalidateRefs();
    this.#measureHeight();
  }

  // --- Sticky ---

  #setupSticky(mode) {
    this.#sentinel = document.createElement('div');
    this.#sentinel.style.cssText = 'height:1px;width:100%;pointer-events:none';
    const section = this.closest('.section-header') || this.parentElement;
    section.parentElement.insertBefore(this.#sentinel, section);

    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        const scrolled = !entry.isIntersecting;
        this.classList.toggle('is-scrolled', scrolled);
        this.#swapColorScheme(scrolled);
      },
      { threshold: 0 }
    );
    this.#stickyObserver.observe(this.#sentinel);

    if (mode === 'on_scroll_up') {
      this.#setupScrollDirection();
    }
  }

  #setupScrollDirection() {
    this.#scrollY = window.scrollY;

    window.addEventListener('scroll', () => {
      const currentY = window.scrollY;
      const delta = currentY - this.#scrollY;

      if (currentY <= this.#headerHeight) {
        this.classList.remove('is-hidden');
      } else if (delta > 5) {
        this.classList.add('is-hidden');
      } else if (delta < -5) {
        this.classList.remove('is-hidden');
      }

      this.#scrollY = currentY;
    }, { signal: this.signal, passive: true });
  }

  // --- Transparent color scheme swap ---

  #swapColorScheme(scrolled) {
    const header = this.querySelector('.header');
    if (!header) return;
    const transparentScheme = header.dataset.transparentScheme;
    const defaultScheme = header.dataset.defaultScheme;
    if (!transparentScheme || !defaultScheme) return;

    if (scrolled) {
      header.classList.remove(transparentScheme);
      header.classList.add(defaultScheme);
    } else {
      header.classList.remove(defaultScheme);
      header.classList.add(transparentScheme);
    }
  }

  // --- Height tracking ---

  #measureHeight() {
    this.#headerHeight = this.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${this.#headerHeight}px`);
  }

  #setupResizeObserver() {
    this.#resizeObserver = new ResizeObserver(() => {
      this.debounce('resize', () => this.#measureHeight(), 100);
    });
    this.#resizeObserver.observe(this);
  }

  // --- Cart count ---

  #updateCartCount(event) {
    const badge = this.ref('cart-count');
    if (!badge) return;
    const count = event.cart?.item_count ?? 0;
    badge.textContent = count;
    badge.hidden = count === 0;
  }

  // --- Search overlay ---

  openSearch() {
    this.querySelector('scale-search-overlay')?.openSearch();
  }

  closeSearch() {
    this.querySelector('scale-search-overlay')?.closeSearch();
  }

  clearSearch() {
    this.querySelector('scale-search-overlay')?.clearSearch();
  }

  // --- Drawers ---

  async openDrawer() {
    await this.#openElement('scale-drawer', document.getElementById('header-drawer'));
  }

  async openCartDrawer(event) {
    event.preventDefault();
    await this.#openElement('scale-cart-drawer', document.querySelector('scale-cart-drawer'));
  }

  async #openElement(tagName, element) {
    if (!element) return;
    if (!customElements.get(tagName)) {
      await customElements.whenDefined(tagName);
    }
    element.open();
  }

  // --- Localization ---

  submitLocalization(event) {
    event.target.closest('form')?.submit();
  }

  // --- Design mode ---

  onBlockSelect() {
    document.getElementById('header-drawer')?.open?.();
  }

  onBlockDeselect() {
    document.getElementById('header-drawer')?.close?.();
  }
}

if (!customElements.get('scale-header')) {
  customElements.define('scale-header', ScaleHeader);
}
